import { z } from 'zod';

import {
  createEntityAdapterSchema,
  findDuplicateIds,
  findDuplicateNames,
} from '../../base';
import {
  AttributeSchema,
  VariantAttributesValuesSchema,
  VariantSchema,
} from './variant.schema';

export const VariantManagementSchema = z
  .object({
    variants: createEntityAdapterSchema(VariantSchema),
    attributes: createEntityAdapterSchema(AttributeSchema),
    variantAttributeValues: createEntityAdapterSchema(
      VariantAttributesValuesSchema,
    ),
  })
  .superRefine((data, ctx) => {
    const variants = Object.values(data.variants.entities);
    const duplicateVariantNames = findDuplicateNames(variants);
    const duplicateVariantIds = findDuplicateIds(variants);
    for (const duplicate of duplicateVariantNames) {
      ctx.addIssue({
        path: ['variants', 'entities', duplicate.id, 'name'],
        code: z.ZodIssueCode.custom,
        message: 'Duplicate name',
      });
    }
    for (const duplicate of duplicateVariantIds) {
      ctx.addIssue({
        path: ['variants', 'entities', duplicate.id, 'id'],
        code: z.ZodIssueCode.custom,
        message: 'Duplicate ID',
      });
    }

    const attributes = Object.values(data.attributes.entities).filter(
      attribute => !attribute.archived,
    );
    const duplicateAttributeNames = findDuplicateNames(attributes);
    const duplicateAttributeIds = findDuplicateIds(attributes);
    for (const duplicate of duplicateAttributeNames) {
      ctx.addIssue({
        path: ['attributes', 'entities', duplicate.id, 'name'],
        code: z.ZodIssueCode.custom,
        message: 'Duplicate name',
      });
    }
    for (const duplicate of duplicateAttributeIds) {
      ctx.addIssue({
        path: ['attributes', 'entities', duplicate.id, 'id'],
        code: z.ZodIssueCode.custom,
        message: 'Duplicate ID',
      });
    }

    // Validate that exactly one variant is marked as default (if any variants exist)
    const defaultVariants = variants.filter(v => v.isDefault);

    if (variants.length > 0 && defaultVariants.length === 0) {
      ctx.addIssue({
        path: ['variants'],
        code: z.ZodIssueCode.custom,
        message: 'At least one variant must be marked as default',
      });
    }

    if (defaultVariants.length > 1) {
      // Mark ALL variants with isDefault: true as errors so user can see and pick one
      defaultVariants.forEach(variant => {
        ctx.addIssue({
          path: ['variants', 'entities', variant.id, 'isDefault'],
          code: z.ZodIssueCode.custom,
          message: `Multiple variants are marked as default. Please select only one default variant.`,
        });
      });
    }
  });

export type VariantManagement = z.infer<typeof VariantManagementSchema>;
